import {
  type Algorithm,
  type Hasher,
  type PromiseFactory,
  type PromiseHandler,
  type SetCertificatePromiseOptions,
} from "qz-tray";

import { qz } from "./client";

export type BootstrapQZOptions = {
  /** Optional extra flags forwarded to `qz.security.setCertificatePromise`. */
  certificateOptions?: SetCertificatePromiseOptions;
  /**
   * Returns the client's signed certificate in PEM form. Must be installed
   * before any connection attempt. See qz-tray docs for `setCertificatePromise`.
   */
  getCertificate: PromiseHandler;
  /**
   * Optional QZ request hasher. Supplying this lets an application expose the
   * serialized signing envelope to its signature service instead of the
   * default SHA-256 digest. It must be installed before QZ is initialized.
   *
   * QZ Tray invokes this hook synchronously while preparing the message to
   * sign, so it must return the hash as a string.
   */
  hasher?: (envelope: string) => string;
  /**
   * Called by qz-tray to sign a server-provided nonce. Return the signature
   * as a string. A plain `(data) => Promise<string>` works too; the library
   * wraps it into a promise factory.
   */
  sign: ((dataToSign: string) => Promise<string>) | PromiseFactory;
  /** Signature algorithm for `setSignatureAlgorithm`. Defaults to SHA-512. */
  signatureAlgorithm?: Algorithm;
};

let bootstrapped = false;

/**
 * One-time wiring of the qz-tray security pipeline. Idempotent: repeat calls
 * after the first are ignored so HMR / route remounts don't re-install the
 * callbacks.
 */
export const bootstrapQZ = (options: BootstrapQZOptions) => {
  if (bootstrapped) return;

  bootstrapped = true;

  if (options.hasher) {
    // @types/qz-tray models this legacy hook as callback-only, but QZ Tray
    // 2.2.6 accepts the direct or promised string results used here.
    qz.api.setSha256Type(options.hasher as unknown as Hasher);
  }
  qz.security.setSignatureAlgorithm(options.signatureAlgorithm ?? "SHA512");
  qz.security.setCertificatePromise(
    options.getCertificate,
    options.certificateOptions,
  );
  qz.security.setSignaturePromise(options.sign);
};
